import {Component, Prop} from 'vue-property-decorator';
import {CaseItemDto} from '@/model/CommonModel';
import CaseApi from '@/api/CaseApi';
import DefaultComponentMixin from '@/views/DefaultComponentMixin';

@Component
export default class CaseInputMixin extends DefaultComponentMixin {
    @Prop({default: ''}) protected readonly id: string;
    @Prop({default: ''}) protected readonly storeId: string;
    @Prop({default: false}) protected readonly disabled: boolean;
    /**
     * Html의 readonly attribute를 활성화 시킬 지 여부
     */
    @Prop({default: false}) protected readonly readonly: boolean;
    /**
     * 각 Case Item에서 사용자에 의해 변경사항이 발행할 경우 @input event를 emit 시킬 지 여부
     * mute off인 경우 사용자의 입력을 핸들링 하는 콜백 e.g (onChangeInput)에서 @input 이벤트와 store에 commit이 제한되어야 한다.
     * 단, erase, notation, nullflavor 등으로 인한 데이터 변경은 가능해야 한다.
     * @see CaseTextInput
     * CaseTextInput에만 구현되어있음
     */
    @Prop({default: false}) protected readonly mute: boolean;
    @Prop({default: false}) protected readonly autofocus: boolean;
    @Prop({default: false}) protected readonly clearable: boolean;
    @Prop({default: false}) protected readonly newItem: boolean;
    
    @Prop({default: false}) protected readonly hasNullflavor: boolean;
    @Prop({default: false}) protected readonly hasNotation: boolean;
    @Prop({default: true}) protected readonly hasItemAction: boolean;
    @Prop({default: false}) protected readonly blindVisible: boolean;
    @Prop({default: undefined}) protected readonly maxLength: number;
    
    @Prop({default: null}) protected readonly value: CaseItemDto | any;
    @Prop({default: null}) protected readonly blindValue: CaseItemDto | any;
    @Prop({default: ''}) protected readonly placeHolder: string;
    @Prop({default: ''}) protected readonly size: string;
    /**
     * Nullflavor, Code 목록 등을 조회 시 필요
     */
    @Prop({default: ''}) protected readonly itemId: string;
    @Prop({default: true}) private readonly autoSizeable: boolean;
    @Prop({default: 1}) private readonly minRows: number;
    @Prop({default: Infinity}) private readonly maxRows: number;
    
    protected scrollFocused: boolean = false;

    
    get getId() {
        return this.value.itemId;
    }
    
    get isDisabled() {
        return this.disabled;
    }
    
    get isReadOnly() {
        return this.readonly;
    }
    
    get icClearable() {
        return !this.readonly && !this.mute;
    }
    
    get isMute() {
        return this.mute;
    }
    
    get isActive() {
        return !this.isDisabled && !this.isReadOnly;
    }
    
    get supplyAutofocus() {
        return this.autofocus;
    }
    
    get isClearable() {
        return this.clearable;
    }
    
    get supplyNullFlavor() {
        return this.hasNullflavor;
    }
    
    get supplyNotation() {
        return this.hasNotation;
    }
    
    get supplyItemAction() {
        return this.hasItemAction;
    }
    
    get supplyUnblind() {
        return this.blindVisible;
    }
    
    get inputValue() {
        return this.value.value;
    }
    
    get blindInputValue() {
        return this.blindValue.value;
    }
    
    get getMaxLength() {
        return this.maxLength;
    }
    
    get getPlaceHolder() {
        return this.placeHolder;
    }
    
    get getItemId() {
        return this.itemId ? this.itemId : this.value.itemId;
    }
    
    get getSize() {
        return this.size;
    }
    
    get isNew() {
        return this.newItem;
    }
    
    get isScrollFocused() {
        return this.scrollFocused;
    }

    get isAutosizeable() {
        return this.autoSizeable ? {minRows: this.minRows, maxRows: this.maxRows} : false;
    }
    
    get itemClasses() {
        return {
            'scroll-focused': this.scrollFocused
        };
    }

    get getUnblindPlaceholder() {
        return 'Unblind';
    }

    public getCaseInputId(inputDto: CaseItemDto): string {
        return this.getStoreId(inputDto);
    }
    
    public getCaseBlindInputId(inputDto: CaseItemDto): string {
        return this.getBlindStoreId(inputDto);
    }
    
    public getAuditTrailColumns(dateTime: string, user: string, item: string, value: string,
                                notation: string, nullFlavor: string, reason: string) {
        return [
            {columnId: 'time', columnLabel: dateTime, visible: 'Y', width: '192px'},
            {columnId: 'userId', columnLabel: user, visible: 'Y', width: '180px'},
            {columnId: 'itemLabel', columnLabel: item, visible: 'Y', width: '180px'},
            {columnId: 'value', columnLabel: value, visible: 'Y', width: '200px'},
            {columnId: 'notation', columnLabel: notation, visible: 'Y', width: '200px'},
            {columnId: 'nullFlavor', columnLabel: nullFlavor, visible: 'Y', width: '140px'},
            {columnId: 'reason', columnLabel: reason, visible: 'Y', width: '200px'}
        ];
    }
    
    public onAuditTrail() {
        CaseApi.getAuditTrail(+this.$route.params.caseKey, this.$route.meta.pageId, this.value.rowNo,
            this.value.rowNo2, this.value.rowNo3, this.value.itemId)
               .then((res: any) => {
                   this.$modal.showAuditTrailDialog({
                       auditTrailList: res.data.auditTrail,
                       columnList: this.getAuditTrailColumns(
                           this.$t('message.auditTrailDialog.dateTime').toString(),
                           this.$t('message.auditTrailDialog.user').toString(),
                           this.$t('message.auditTrailDialog.item').toString(),
                           this.$t('message.auditTrailDialog.value').toString(),
                           this.$t('message.auditTrailDialog.notation').toString(),
                           this.$t('message.auditTrailDialog.nullFlavor').toString(),
                           this.$t('message.auditTrailDialog.reason').toString()),
                       width: '1400px'
                   });
               });
    }
    
    public onBlindAuditTrail() {
        CaseApi.getBlindAuditTrails(+this.$route.params.caseKey, this.$route.meta.pageId, this.value.rowNo,
            this.value.rowNo2, this.value.rowNo3, this.value.itemId)
               .then((res: any) => {
                   this.$modal.showAuditTrailDialog({
                       auditTrailList: res.data.auditTrail,
                       columnList: this.getAuditTrailColumns(
                           this.$t('message.auditTrailDialog.dateTime').toString(),
                           this.$t('message.auditTrailDialog.user').toString(),
                           this.$t('message.auditTrailDialog.item').toString(),
                           this.$t('message.auditTrailDialog.value').toString(),
                           this.$t('message.auditTrailDialog.notation').toString(),
                           this.$t('message.auditTrailDialog.nullFlavor').toString(),
                           this.$t('message.auditTrailDialog.reason').toString()),
                       width: '1400px'
                   });
               });
    }
    
    public getStoreId(inputDto: CaseItemDto): string {
        return this.storeId ? this.storeId : `${inputDto.itemId}-${inputDto.rowNo}-${inputDto.rowNo2}-${inputDto.rowNo3}`;
    }
    
    public commitValue(value: CaseItemDto, valueString: string) {
        this.$store.commit('commitValue', {
            itemId: this.getStoreId(value),
            value: valueString,
            originValue: value.originValue
        });
        this.$store.commit('commitNullFlavor', {
            itemId: this.getStoreId(value),
            value: '',
            originValue: value.originNullFlavor
        });
    }
    
    public commitNullFlavor(value: CaseItemDto, nullFlavor: string) {
        this.$store.commit('commitValue', {
            itemId: this.getStoreId(value),
            value: '',
            originValue: value.originValue
        });
        this.$store.commit('commitNotation', {
            itemId: this.getStoreId(value),
            value: '',
            originValue: value.originNotation
        });
        this.$store.commit('commitNullFlavor', {
            itemId: this.getStoreId(value),
            value: nullFlavor,
            originValue: value.originNullFlavor
        });
    }
    
    public commitNotation(value: CaseItemDto, notation: string) {
        this.$store.commit('commitNotation', {
            itemId: this.getStoreId(value),
            value: notation,
            originValue: value.originNotation
        });
        this.$store.commit('commitNullFlavor', {
            itemId: this.getStoreId(value),
            value: '',
            originValue: value.originNullFlavor
        });
    }
    
    public getBlindStoreId(inputDto: CaseItemDto): string {
        return `BLIND-${this.getStoreId(inputDto)}`;
    }
    
    public commitBlindValue(blindValue: CaseItemDto, valueString: string) {
        this.$store.commit('commitValue', {
            itemId: this.getBlindStoreId(blindValue),
            value: valueString,
            originValue: blindValue.originValue
        });
        this.$store.commit('commitNullFlavor', {
            itemId: this.getBlindStoreId(blindValue),
            value: '',
            originValue: blindValue.originNullFlavor
        });
    }
    
    public commitBlindNullFlavor(blindValue: CaseItemDto, nullFlavor: string) {
        this.$store.commit('commitValue', {
            itemId: this.getBlindStoreId(blindValue),
            value: '',
            originValue: blindValue.originValue
        });
        this.$store.commit('commitNotation', {
            itemId: this.getBlindStoreId(blindValue),
            value: '',
            originValue: blindValue.originNotation
        });
        this.$store.commit('commitNullFlavor', {
            itemId: this.getBlindStoreId(blindValue),
            value: nullFlavor,
            originValue: blindValue.originNullFlavor
        });
    }
    
    public focus(delay?: number) {
        let component: any = this.$refs.elementRef;
        if (component instanceof Array) {
            component = component[0];
        }
        
        if (delay) {
            setTimeout(() => {
                if (component && component.focus) {
                    component.focus();
                }
            }, 100);
        } else {
            if (component && component.focus) {
                component.focus();
            }
        }
    }

    public onFocus() {
        this.trimToMaxLength();
    }
    
    public moveToScroll(itemId: string) {
        this.scrollFocused = true;
        const storeId = this.getStoreId(this.value);
        const element = document.getElementById(storeId);
        if (element) {
            console.log(`Find HTML element `, element);
            element.scrollIntoView();
        } else {
            console.warn(`Cannot find HTML element [${storeId}]`, element);
        }
    }
    
    public blur(delay?: number) {
        let component: any = this.$refs.elementRef;
        if (component instanceof Array) {
            component = component[0];
        }
        
        if (delay) {
            setTimeout(() => component.blur(), 100);
        } else {
            component.blur();
        }
    }

    protected trimToMaxLength() {
        if (this.value.value.length > this.maxLength) {
            this.commitValue(this.value, this.value.value.substring(0, this.maxLength));

            this.$emit('input', {
                ...this.value,
                ['value']: this.value.value.substring(0, this.maxLength),
                ['nullFlavor']: ''
            });
        }
    }

    protected dictionaryRowDataCommit(data: any) {
        if (this.isReadOnly) {
            return;
        }

        this.$emit('input', {
            ...this.value,
            ['value']: data,
            ['notation']: this.value.notation,
        });
        this.$emit('selected', data);

        this.commitValue(this.value, data);
    }
    
    protected onEnter($event: KeyboardEvent) {
        this.$emit('enter', $event);
    }
    
    protected onBlur($event: FocusEvent) {
        this.$emit('blur', $event);
    }
    
    protected onMouseup($event: MouseEvent) {
        this.$emit('mouseup', $event);
    }
    
    protected onClear() {
        this.$emit('clear');
    }
    
    protected init() {
        this.commitValue(this.value, '');
        this.commitNullFlavor(this.value, '');
        this.commitNotation(this.value, '');
    }
    
    protected initBlind() {
        this.commitBlindValue(this.blindValue, '');
        this.commitBlindNullFlavor(this.blindValue, '');
    }
}
